<script setup>
import { computed, ref, watch } from 'vue';
import Input from './Input.vue';
import { useI18n } from 'vue-i18n';
import { DURATION_UNITS } from './constants';

const props = defineProps({
  min: { type: Number, default: 0 },
  max: { type: Number, default: Infinity },
  disabled: { type: Boolean, default: false },
});

const { t } = useI18n();
const duration = defineModel('modelValue', { type: Number, default: null });
const unit = defineModel('unit', {
  type: String,
  default: DURATION_UNITS.MINUTES,
  validate(value) {
    return Object.values(DURATION_UNITS).includes(value);
  },
});

const UNIT_IN_MINUTES = {
  [DURATION_UNITS.MINUTES]: 1,
  [DURATION_UNITS.HOURS]: 60,
  [DURATION_UNITS.DAYS]: 24 * 60,
};

const limitMessage = ref('');

// The largest unit the duration fits into without a fraction, so that
// 900 minutes reads as "15 hours" instead of collapsing to "0 days"
const fittingUnit = minutes => {
  if (!minutes) return unit.value;
  if (minutes % UNIT_IN_MINUTES[DURATION_UNITS.DAYS] === 0) {
    return DURATION_UNITS.DAYS;
  }
  if (minutes % UNIT_IN_MINUTES[DURATION_UNITS.HOURS] === 0) {
    return DURATION_UNITS.HOURS;
  }
  return DURATION_UNITS.MINUTES;
};

const clampToLimits = minutes => {
  if (minutes > props.max) {
    limitMessage.value = t('DURATION_INPUT.MAX_REACHED');
    return props.max;
  }
  if (minutes < props.min) {
    limitMessage.value = t('DURATION_INPUT.MIN_REACHED');
    return props.min;
  }
  limitMessage.value = '';
  return minutes;
};

// Keeps the duration intact: only the way it is shown changes
const transformedValue = computed({
  get() {
    if (duration.value == null) return null;
    return duration.value / UNIT_IN_MINUTES[unit.value];
  },
  set(newValue) {
    if (newValue == null || newValue === '') {
      duration.value = null;
      limitMessage.value = '';
      return;
    }

    duration.value = clampToLimits(
      Math.round(newValue * UNIT_IN_MINUTES[unit.value])
    );
  },
});

// Pick the unit by the scale of the value — when it arrives from the outside
// and once the user is done typing, never mid-input
const pickFittingUnit = () => {
  if (duration.value == null) return;
  unit.value = fittingUnit(duration.value);
};

watch(duration, pickFittingUnit, { immediate: true });
</script>

<template>
  <Input
    v-model="transformedValue"
    type="number"
    autocomplete="off"
    :disabled="disabled"
    :placeholder="t('DURATION_INPUT.PLACEHOLDER')"
    :message="limitMessage"
    :message-type="limitMessage ? 'error' : 'info'"
    class="flex-grow w-full disabled:"
    @blur="pickFittingUnit"
  />
  <select
    v-model="unit"
    :disabled="disabled"
    class="mb-0 text-sm disabled:outline-n-weak disabled:opacity-40"
  >
    <option :value="DURATION_UNITS.MINUTES">
      {{ t('DURATION_INPUT.MINUTES') }}
    </option>
    <option :value="DURATION_UNITS.HOURS">
      {{ t('DURATION_INPUT.HOURS') }}
    </option>
    <option :value="DURATION_UNITS.DAYS">
      {{ t('DURATION_INPUT.DAYS') }}
    </option>
  </select>
</template>
